import type {
  CheckoutInput,
  CheckoutSession,
  PaymentGatewayCode,
  PaymentGatewayConfig,
} from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

export const paymentGateways: PaymentGatewayConfig[] = [
  {
    id: 'gateway-stripe',
    code: 'stripe',
    name: 'Stripe',
    enabled: true,
    mode: 'test',
    priority: 10,
    supportsHostedCheckout: true,
    supportsRefunds: true,
    supportsWebhooks: true,
    publicConfig: {},
    status: 'healthy',
    health: 'green',
    supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD'],
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
    onboardingHint: 'Connect your Stripe account to accept credit cards and digital wallets.',
    webhookStatus: 'receiving',
  },
  {
    id: 'gateway-paypal',
    code: 'paypal',
    name: 'PayPal',
    enabled: true,
    mode: 'test',
    priority: 20,
    supportsHostedCheckout: true,
    supportsRefunds: true,
    supportsWebhooks: true,
    publicConfig: {},
    status: 'missing_keys',
    health: 'yellow',
    supportedCurrencies: ['USD', 'EUR', 'GBP', 'JPY'],
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg',
    onboardingHint: 'Enable PayPal to allow customers to pay with their PayPal balance or credit cards.',
  },
  {
    id: 'gateway-paddle',
    code: 'paddle',
    name: 'Paddle',
    enabled: false,
    mode: 'test',
    priority: 30,
    supportsHostedCheckout: true,
    supportsRefunds: true,
    supportsWebhooks: true,
    publicConfig: {},
    status: 'offline',
    health: 'red',
    supportedCurrencies: ['USD', 'EUR', 'GBP'],
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/a/a3/Paddle_Logo.svg',
    onboardingHint: 'Paddle handles all your taxes and compliance automatically.',
  },
  {
    id: 'gateway-2checkout',
    code: '2checkout',
    name: '2Checkout / Verifone',
    enabled: true,
    mode: 'test',
    priority: 40,
    supportsHostedCheckout: true,
    supportsRefunds: true,
    supportsWebhooks: true,
    publicConfig: {},
    status: 'connected',
    health: 'green',
    supportedCurrencies: ['USD', 'EUR', 'GBP'],
    logoUrl: 'https://upload.wikimedia.org/wikipedia/commons/5/5a/2Checkout_Logo.svg',
    onboardingHint: 'Accept global payments with 2Checkout (now Verifone).',
  },
  {
    id: 'gateway-sslcommerz',
    code: 'sslcommerz',
    name: 'SSLCommerz',
    enabled: false,
    mode: 'test',
    priority: 50,
    supportsHostedCheckout: true,
    supportsRefunds: true,
    supportsWebhooks: true,
    publicConfig: {},
    status: 'invalid_credentials',
    health: 'red',
    supportedCurrencies: ['BDT'],
    logoUrl: 'https://securepay.sslcommerz.com/gw/images/logo/sslcommerz.png',
    onboardingHint: 'The largest payment gateway in Bangladesh.',
  },
  {
    id: 'gateway-crypto',
    code: 'crypto',
    name: 'Cryptocurrency',
    enabled: true,
    mode: 'test',
    priority: 45,
    supportsHostedCheckout: false,
    supportsRefunds: false,
    supportsWebhooks: false,
    publicConfig: {},
    status: 'connected',
    health: 'green',
    supportedCurrencies: ['USD', 'EUR', 'GBP'],
    logoUrl: '',
    onboardingHint: 'Accept Bitcoin, Ethereum, USDC, and other cryptocurrencies.',
  },
  {
    id: 'gateway-manual',
    code: 'manual',
    name: 'Manual Payment',
    enabled: true,
    mode: 'test',
    priority: 100,
    supportsHostedCheckout: false,
    supportsRefunds: false,
    supportsWebhooks: false,
    publicConfig: {
      instructionsEnabled: true,
    },
    status: 'connected',
    health: 'green',
    supportedCurrencies: ['USD', 'BDT', 'EUR'],
    logoUrl: '',
    onboardingHint: 'Allow customers to pay via bank transfer or other manual methods.',
  },
];

export function getPaymentGatewayByCode(code: PaymentGatewayCode) {
  return paymentGateways.find((gateway) => gateway.code === code);
}

export function getEnabledPaymentGateways(enabledCodes?: string[]) {
  if (enabledCodes && enabledCodes.length > 0) {
    return paymentGateways
      .filter((g) => enabledCodes.includes(g.code))
      .sort((a, b) => a.priority - b.priority);
  }
  return paymentGateways
    .filter((gateway) => gateway.enabled)
    .sort((a, b) => a.priority - b.priority);
}

export async function createCheckoutSession(
  input: CheckoutInput,
): Promise<CheckoutSession> {
  const supabase = requireSupabase();

  // Create order as pending + license as inactive (payment not yet received)
  const { data, error } = await supabase.rpc('customer_create_order', {
    p_product_id: input.productId,
    p_plan_id: input.planId,
  });

  if (error) throw new Error(error.message);

  const orderId = (data as any).order_id;
  const orderRef = (data as any).order_ref;
  const amount = (data as any).amount;
  const productName = (data as any).product_name;
  const planName = (data as any).plan_name;

  // For crypto/NOWPayments — redirect to NOWPayments payment page
  if (input.gatewayCode === 'crypto') {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) throw new Error('Supabase URL is not configured.');

    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const res = await fetch(
      `${supabaseUrl}/functions/v1/create-crypto-payment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId,
          orderRef,
          amount,
          currency: 'USD',
          productName,
          planName,
        }),
      },
    );

    const npData = await res.json();
    if (!res.ok) throw new Error(npData.error || 'Failed to create crypto payment.');

    return {
      id: orderId,
      gatewayCode: 'crypto',
      redirectUrl: npData.paymentUrl,
    };
  }

  // For all other gateways — redirect to orders page
  return {
    id: orderId,
    gatewayCode: input.gatewayCode,
    redirectUrl: input.successUrl,
  };
}
